import type React from "react";
import { Card, CardContent } from "../../ui/card";
import { Tabs, TabsContent/* , TabsList, TabsTrigger */ } from "../../ui/tabs";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DollarSign,
  Package,
  ShoppingCart,
  Users,
} from "lucide-react";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { cn } from "../../../utils/utils";
import SalesLoader from "../../../components/loaders/SalesLoader";
import type { DateRange } from "react-day-picker";
import { startOfMonth, endOfMonth, format/* , subMonths  */} from "date-fns";
import { useWholesaleSales } from "../hooks/useSalesWholesale";
import { useMemo } from "react";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatNumber } from "../../../utils/formatNumber";




interface SalesMetricProps {
  title: string;
  value: string;
  description: string;
  icon: React.ReactNode;
  change: number;
  changeLabel: string;
  showChange?: boolean;
  isLoading?: boolean;
}

function SalesMetric({
  title,
  value,
  description,
  icon,
  change,
  changeLabel,
  showChange = true, // default to true if not provided
  isLoading = false,
}: SalesMetricProps) {








  return (
    <Card>
      {isLoading ? (
        <SalesLoader variant="sales-kpi" itemCount={4} />
      ) : (
        <CardContent>
          <div className="flex items-center justify-between pb-2">
              <p className="text-sm font-medium text-muted-foreground">
                {title}
              </p>
              <div className="h-4 w-4 text-muted-foreground">{icon}</div>
            </div>
          <div className="text-2xl font-bold">{value}</div>
          <p className="text-xs text-muted-foreground">{description}</p>
          {showChange && (
            <div className="mt-2 flex items-center text-xs">
              {change > 0 ? (
                <ArrowUpIcon className="mr-1 h-3 w-3 text-emerald-500" />
              ) : (
                <ArrowDownIcon className="mr-1 h-3 w-3 text-rose-500" />
              )}
              <span className={cn(change > 0 ? "text-emerald-500" : "text-rose-500")}>
                {Math.abs(change).toFixed(1)}%
              </span>
              <span className="ml-1 text-muted-foreground">{changeLabel}</span>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

interface SalesOverviewProps {
  selectedMonth: string; // Accepts "yyyy-MM" format
  selectedRange?: DateRange;
}

export function WholesaleSalesCard({ selectedMonth }: SalesOverviewProps) {
  // Calculate startDate and endDate for the selected month
  const monthDate = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }, [selectedMonth]);

  const startDate = format(startOfMonth(monthDate), "yyyy-MM-dd");
  const endDate = format(endOfMonth(monthDate), "yyyy-MM-dd");
  
    /* // Calculate startDate and endDate for the previous month
    const previousStart = format(
      startOfMonth(subMonths(new Date(selectedMonth), 1)),
      "yyyy-MM-dd"
    );
    const previousEnd = format(
      endOfMonth(subMonths(new Date(selectedMonth), 1)),
      "yyyy-MM-dd"
    ); */


  const { data, isLoading } = useWholesaleSales(startDate, endDate);


/* const formatRange = (start?: string, end?: string) =>
    start && end ? `${format(new Date(start), "MMM d")} – ${format(new Date(end), "MMM d")}` : ""; */



  const transformed = useMemo(() => {
  if (!data) {
    return {
      currentPeriod: { revenue: 0, orders: 0, units: 0, averageOrderValue: 0 },
      previousPeriod: { revenue: 0, orders: 0, units: 0, averageOrderValue: 0 },
      growth: { revenue: 0, orders: 0, units: 0, averageOrderValue: 0 },
    };
  }

  return {
    currentPeriod: {
      revenue: data.total_revenue ?? 0,
      orders: data.total_orders ?? 0,
      units: data.total_units_sold ?? 0,
      averageOrderValue: data.average_order_value ?? 0,
    },
    previousPeriod: {
      revenue: data.prev_total_revenue ?? 0,
      orders: data.prev_total_orders ?? 0,
      units: data.prev_total_units_sold ?? 0,
      averageOrderValue: data.prev_average_order_value ?? 0,
    },
    growth: {
      revenue: data.revenue_growth ?? 0,
      orders: data.order_growth ?? 0,
      units: data.units_growth ?? 0,
      averageOrderValue: data.order_growth ?? 0,
    },
  };
}, [data]);

  return (
    <>
      <SummaryHeader
        title="Quick Summary"
      />
      {/* <div className="mb-4 space-y-1">
        <h2 className="text-sm font-medium text-muted-foreground">
          Selected: {formatRange(startDate, endDate)}
        </h2>
        <h2 className="text-sm font-medium text-muted-foreground">
          Comparing to: {formatRange(previousStart, previousEnd)}
        </h2>
      </div> */}

      <Tabs defaultValue="current">
        {/* <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="current">Current Month</TabsTrigger>
            <TabsTrigger value="previous">Previous Month</TabsTrigger>
            <TabsTrigger value="comparison">Comparison</TabsTrigger>
          </TabsList>
        </div> */}
        <TabsContent
          value="current"
          className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
        >

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">

            <SalesMetric
              title="Total Revenue"
              value={formatCurrency(transformed.currentPeriod.revenue)}
              description="Total sales revenue"
              icon={<DollarSign className="h-4 w-4" />}
              change={transformed.growth.revenue}
              changeLabel="vs previous period"
              showChange={false} 
              isLoading={isLoading}
              />
              <SalesMetric
              title="Orders"
              value={formatNumber(transformed.currentPeriod.orders)}
              description="Total orders placed"
              icon={<ShoppingCart className="h-4 w-4" />}
              change={transformed.growth.orders}
              changeLabel="vs previous period"
              showChange={false} 
              isLoading={isLoading}
              />
              <SalesMetric
              title="Units Sold"
              value={formatNumber(transformed.currentPeriod.units)}
              description="Total units sold"
              icon={<Package className="h-4 w-4" />}
              change={transformed.growth.units}
              changeLabel="vs previous period"
              showChange={false} 
              isLoading={isLoading}
              />
              <SalesMetric
              title="Average Order Value"
              value={formatCurrency(transformed.currentPeriod.averageOrderValue)}
              description="Average revenue per order"
              icon={<Users className="h-4 w-4" />}
              change={transformed.growth.averageOrderValue}
              changeLabel="vs previous period"
              showChange={false} 
              isLoading={isLoading}
              />

          </div>

        </TabsContent>
        <TabsContent
          value="previous"
          className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
        >
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SalesMetric
              title="Total Revenue"
              value={formatCurrency(transformed.previousPeriod.revenue)}
              description="Total sales revenue"
              icon={<DollarSign className="h-4 w-4" />}
              change={0}
              changeLabel="previous period"
              showChange={false}
              isLoading={isLoading}
            />
            <SalesMetric
              title="Orders"
              value={formatNumber(transformed.previousPeriod.orders)}
              description="Total orders placed"
              icon={<ShoppingCart className="h-4 w-4" />}
              change={0}
              changeLabel="previous period"
              showChange={false}
              isLoading={isLoading}
            />
            <SalesMetric
              title="Units Sold"
              value={formatNumber(transformed.previousPeriod.units)}
              description="Total units sold"
              icon={<Package className="h-4 w-4" />}
              change={0}
              changeLabel="previous period"
              showChange={false}
              isLoading={isLoading}
            />
            <SalesMetric
              title="Average Order Value"
              value={formatCurrency(transformed.previousPeriod.averageOrderValue)}
              description="Average revenue per order"
              icon={<Users className="h-4 w-4" />}
              change={0}
              changeLabel="previous period"
              showChange={false}
              isLoading={isLoading}
            />
          </div>
        </TabsContent>
        <TabsContent
          value="comparison"
          className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
        >
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SalesMetric
              title="Revenue Growth"
              value={`${transformed.growth.revenue > 0 ? "+" : ""
                }${transformed.growth.revenue.toFixed(1)}%`}
              description={`${formatCurrency(
                transformed.currentPeriod.revenue
              )} vs ${formatCurrency(transformed.previousPeriod.revenue)}`}
              icon={<DollarSign className="h-4 w-4" />}
              change={transformed.growth.revenue}
              changeLabel="growth rate"
              isLoading={isLoading}
            />
            <SalesMetric
              title="Order Growth"
              value={`${transformed.growth.orders > 0 ? "+" : ""
                }${transformed.growth.orders.toFixed(1)}%`}
              description={`${formatNumber(
                transformed.currentPeriod.orders
              )} vs ${formatNumber(transformed.previousPeriod.orders)}`}
              icon={<ShoppingCart className="h-4 w-4" />}
              change={transformed.growth.orders}
              changeLabel="growth rate"
              isLoading={isLoading}
            />
            <SalesMetric
              title="Units Growth"
              value={`${transformed.growth.units > 0 ? "+" : ""
                }${transformed.growth.units.toFixed(1)}%`}
              description={`${formatNumber(
                transformed.currentPeriod.units
              )} vs ${formatNumber(transformed.previousPeriod.units)}`}
              icon={<Package className="h-4 w-4" />}
              change={transformed.growth.units}
              changeLabel="growth rate"
              isLoading={isLoading}
            />
            <SalesMetric
              title="AOV Growth"
              value={`${transformed.growth.averageOrderValue > 0 ? "+" : ""
                }${transformed.growth.averageOrderValue.toFixed(1)}%`}
              description={`${formatCurrency(
                transformed.currentPeriod.averageOrderValue
              )} vs ${formatCurrency(transformed.previousPeriod.averageOrderValue)}`}
              icon={<Users className="h-4 w-4" />}
              change={transformed.growth.averageOrderValue}
              changeLabel="growth rate"
              isLoading={isLoading}
            />
          </div>
        </TabsContent>
      </Tabs>
    </>
  );
}
